import { Skeleton } from '../Skeleton';
import { Tooltip } from '../Tooltip';

/** One tile: a facet VALUE on one axis, its label, and the server's count. */
export interface IndexAlertTile {
  /** The facet value, exactly as the server emits it. */
  value: string;
  label: string;
  /** Null where nothing can answer it yet — rendered as an em dash, never a zero. */
  count: number | null;
  /**
   * A colour MARKER before the label — a dot class such as `bg-red-500` — where the tile is
   * also the page's colour LEGEND (Building Management's aging bands). The word still carries
   * the meaning; the dot is `aria-hidden`.
   */
  marker?: string;
}

/**
 * A strip of alert tiles above an index table — each a COUNT on one facet axis that narrows the
 * list to that value when pressed. For `<IndexPage aboveTable>`.
 *
 * **The shell owns the selection; this only draws it.** A tile reports the server's facet count
 * and replaces the axis's selection through `onSelect`, so the tiles, the folder tabs and the
 * filter rail are three views of one state and cannot disagree about what the list shows.
 *
 * Drawn to `<StockLevelAlerts>`' own specification, which predates it and still carries its own
 * copy because its tiles span TWO axes; a single-axis strip is this one.
 *
 * - **A tile reading zero is disabled with a reason** — pressing it could only empty the table.
 *   A TICKED tile stays pressable whatever it reads, or the reader could not untick it.
 * - **The figure is the darkest thing on the tile** (`tabular-nums`, mono), because it is what
 *   the reader scans for; the selected tile takes the product's selection treatment.
 */
export function IndexAlertTiles({
  tiles,
  selected,
  loading,
  onSelect,
  emptyReason,
}: {
  tiles: IndexAlertTile[];
  /** What is ticked on this axis now. */
  selected: string[];
  /** First answer in flight — the figures skeleton and the tiles stay unpressable. */
  loading: boolean;
  /** Replaces the axis's selection. Never a per-value toggle — the shell owns the state. */
  onSelect: (values: string[]) => void;
  /** Why a zero tile cannot be pressed, in the page's own words. */
  emptyReason: (tile: IndexAlertTile) => string;
}) {
  if (tiles.length === 0) return null;

  const columns = tiles.length <= 2 ? 'sm:grid-cols-2' : tiles.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-4';

  return (
    <div className={`grid grid-cols-2 gap-3 ${columns}`}>
      {tiles.map((tile) => {
        const active = selected.includes(tile.value);
        const emptied = tile.count === 0 && !active;
        const unavailable = tile.count === null || loading || emptied;
        const reason = emptied ? emptyReason(tile) : null;

        const button = (
          <button
            type="button"
            disabled={unavailable}
            aria-pressed={active}
            onClick={() =>
              onSelect(active ? selected.filter((v) => v !== tile.value) : [...selected, tile.value])
            }
            className={[
              'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition',
              unavailable
                ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                : active
                  ? 'border-slate-400 bg-blue-50 text-slate-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
            ].join(' ')}
          >
            <span className="flex min-w-0 items-center gap-2">
              {tile.marker ? (
                <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${tile.marker}`} />
              ) : null}
              <span className="truncate text-xs">{tile.label}</span>
            </span>

            {loading ? (
              <Skeleton className="ml-3 h-4 w-6 shrink-0" />
            ) : (
              <span className="ml-3 shrink-0 font-mono text-sm tabular-nums">
                {tile.count === null ? '—' : tile.count}
              </span>
            )}
          </button>
        );

        // Wrapped only where there is something to say — a tooltip renders a portal.
        return reason && !loading ? (
          <Tooltip key={tile.value} block disabledReason={reason}>
            {button}
          </Tooltip>
        ) : (
          <div key={tile.value}>{button}</div>
        );
      })}
    </div>
  );
}
